import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";
import ActiveInactive from "../../../../components/activeInactive/activeInactive";
import RegistrationsForm from "./registrationsForm";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { toastState } from "../../../../states/toastState";

const Registrations = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [toastConfig, setToastConfig] = useAtom(toastState);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("registrations");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "createdDate",
          type: "date",
        },
        {
          name: "documentNumber",
          type: "string",
        },
        {
          name: "name",
          type: "string",
        },
        {
          name: "lastName",
          type: "string",
        },
        {
          name: "address",
          type: "string",
        },
        {
          name: "phone",
          type: "string",
        },
        {
          name: "answerDate",
          type: "date",
        },
        {
          name: "answer",
          type: "boolean",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "createdDate",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      setToastConfig({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const openFormDialog = async (formMode, selectedRegistrations) => {
    setFormSettings((prev) => ({
      ...prev,
      formMode: formMode,
      entityId: selectedRegistrations?.id,
      hideDialog: hideDialog,
      toast: toast,
      CRUDService: CRUDService,
    }));

    setFormDialog(true);
  };

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      setFormDialog(false);
      // Restablece todos los filtros a sus valores predeterminados
      setFirst(0);
      setRows(100);
      setSortField(null);
      setSortOrder(null);
      setGlobalFilter("");
      // Restablece el valor del campo de entrada
      if (searchInput.current) {
        searchInput.current.value = "";
      }
      // Luego, realiza una nueva búsqueda con los filtros restablecidos
      fetchData();
    }

    setFormDialog(false);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: hideDialog,
    toast: toast,
    CRUDService: CRUDService,
  });

  const actions = (
    <React.Fragment>
      <Button
        label={t("DataTable.New")}
        icon="pi pi-plus"
        className="p-button-primary"
        onClick={() => openFormDialog("CREATE")}
      />
    </React.Fragment>
  );

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex min-w-min justify-content-around">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success mr-2"
          onClick={() => openFormDialog("UPDATE", rowData)}
        />
        {/* <Button
          icon="pi pi-trash"
          className="  p-button-rounded p-button-danger"
          onClick={() => openFormDialog("DELETE", rowData)}
        /> */}
      </div>
    );
  };

  const header = (
    <div>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  const getDialogTitle = () => {
    let action = "Add";

    if (formSettings.formMode == "DELETE") {
      action = "Delete";
    } else if (formSettings.formMode == "UPDATE") {
      action = "Edit";
    }

    return `${t(action)} ${t("entities.registrations.singular")}`;
  };

  const getDialogHeader = () => {
    if (formSettings.formMode === "DELETE") {
      return getDialogTitle();
    }

    return (
      <div className="abm-form-hero">
        <h2 className="abm-form-hero-title">{getDialogTitle()}</h2>
        <p className="abm-form-hero-subtitle">
          Complete la información para gestionar el registro.
        </p>
      </div>
    );
  };

  const answerDateFormat = (rowData) => {
    return helperFormats.isDate(rowData.answerDate)
      ? helperFormats.formatDate(new Date(rowData.answerDate))
      : "";
  };

  const actionAnswer = (rowData) => {
    return (
      <ActiveInactive
        onChange={approveRegistration}
        itemId={rowData.id}
        value={rowData.answer}
        disabled={rowData.answer}
      />
    );
  };

  const approveRegistration = async (id) => {
    setIsLoading(true);

    try {
      const responseApproveRegistration = await api.put(
        `${env("API_URL")}/registrations/approve/${id}`
      );

      fetchData();

      toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
      throw new Error();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-file"></i>}
          title={t("entities.registrations.plural")}
          actions={actions}
        />
      }
    >
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Registrations")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
        >
          <Column
            field="createdDate"
            header={t("entities.registrations.createdDate")}
            sortable
            type="date"
            body={(value) =>
              helperFormats.formatDate(new Date(value.createdDate))
            }
          ></Column>
          <Column
            field="documentNumber"
            header={t("entities.registrations.documentNumber")}
            sortable
          ></Column>
          <Column
            field="name"
            header={t("entities.registrations.name")}
            sortable
          ></Column>
          <Column
            field="lastName"
            header={t("entities.registrations.lastName")}
            sortable
          ></Column>
          <Column
            field="address"
            header={t("entities.registrations.address")}
            sortable
          ></Column>
          <Column
            field="phone"
            header={t("entities.registrations.phone")}
            sortable
          ></Column>
          <Column
            field="answerDate"
            header={t("entities.registrations.answerDate")}
            sortable
            body={answerDateFormat}
          ></Column>
          <Column
            field="answer"
            header={t("entities.registrations.answer")}
            style={{ textAlign: "center" }}
            sortable
            body={actionAnswer}
            exportable={false}
          ></Column>

          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "8rem", textAlign: "center" }}
          ></Column>
        </DataTable>
        <React.Fragment>
          <Dialog
            visible={formDialog}
            header={getDialogHeader()}
            modal
            className="flex w-9"
            onHide={hideDialog}
          >
            <RegistrationsForm settings={formSettings} />
          </Dialog>
        </React.Fragment>
      </div>
    </Layout>
  );
};

export default Registrations;
